import type { FailReason } from '../lib/api';
import type { QuadPoints } from '../render/homography';
import type { TenantConfig } from '../shared/schemas';

/** Starting pattern size: closest to finished floors in customer photos. */
export const DEFAULT_PATTERN_SCALE = 2;

/** Everything about the customer's current photo and choices. */
export interface Session {
  /** Working image, long edge <= 2048 px. */
  photo: HTMLCanvasElement;
  /** JPEG sent to /api/segment; null for samples. */
  upload: Blob | null;
  /** nanoid from /api/segment, or `sample:<id>`. */
  uploadId: string | null;
  spaceTypeId: string;
  /** Floor mask at working-image size (0/255); null while detecting. */
  mask: Uint8Array | null;
  /** Wall mask at working-image size (0/255), for stem walls; null when not detected. */
  wallMask: Uint8Array | null;
  /** Stem wall switched on (tenants with `stemWall` only). */
  stemWall: boolean;
  /** Wall detection: not asked for yet, running, done, or failed. */
  wallStatus: 'idle' | 'loading' | 'ready' | 'failed';
  /** The mask as detected, for "Reset to detected". */
  detectedMask: Uint8Array | null;
  quad: QuadPoints;
  /** True once the customer moved the perspective handles. */
  quadManual: boolean;
  patternScale: number;
  systemId: string;
  colorId: string;
  status: 'detecting' | 'ready' | 'failed';
  /** Why detection failed, when `status` is 'failed'. */
  failReason: FailReason | null;
  lowConfidence: boolean;
}

export function selection(cfg: TenantConfig, s: Pick<Session, 'systemId' | 'colorId' | 'spaceTypeId'>) {
  const system = cfg.systems.find((x) => x.id === s.systemId) ?? cfg.systems[0];
  const color = system.colors.find((x) => x.id === s.colorId) ?? system.colors[0];
  const spaceType = cfg.spaceTypes.find((x) => x.id === s.spaceTypeId) ?? cfg.spaceTypes[0];
  return { system, color, spaceType };
}

/** Starting pattern size for a system: its own, or the default. */
export function startScale(cfg: TenantConfig, systemId: string): number {
  return (cfg.systems.find((x) => x.id === systemId) ?? cfg.systems[0]).patternScale ?? DEFAULT_PATTERN_SCALE;
}
